/**
 * Today as a calendar day — `'2026-09-25'` — in a given IANA zone.
 *
 * The one date operation in the delivery screen that reads a clock. Everything
 * else about a delivery date is UTC-pinned, deliberately: a delivery date
 * names a calendar day and the library renders it as stored, so converting one
 * into a zone west of UTC moves it to the previous day. That is the COM-816
 * defect rather than its fix.
 *
 * What a zone legitimately answers is a different question — *which month does
 * the calendar open on* — and `DeliveryDateCalendar` takes exactly that, as
 * `referenceDate`. It has no `timeZone` prop at all, by design.
 *
 * **`undefined` when no zone is authored, and that is the common case.** The
 * calendar then reads the device's own calendar day, which is the shopper's
 * answer to "what month is it" and is right almost always. The exception this
 * exists for is a storefront whose operating day differs from its shoppers'
 * devices.
 *
 * `Intl.DateTimeFormat` throws a `RangeError` on a malformed zone, and the
 * zone arrives from untyped SCS JSON, so a typo costs the calendar its anchor
 * and never the screen.
 *
 * `en-US` is named rather than left to the runtime default: the format is
 * assembled from parts, so the locale only has to be one whose calendar is
 * Gregorian and whose numerals are Latin. A runtime default of `ar-SA` would
 * answer with a Hijri year.
 */
/**
 * Days between two calendar-day tokens, as a signed integer.
 *
 * Both tokens may be the bare `'YYYY-MM-DD'` or the ISO zoned form
 * `'YYYY-MM-DDTHH:MM:SS.sssZ'` — only the day portion is read, so a delivery
 * token comes through unparsed either way (R-8 holds). `undefined` when either
 * side is malformed; the caller then skips the GA4 `delivery_lead_days` dim.
 */
export const daysBetweenCalendarDays = (
  from?: string | null,
  to?: string | null,
): number | undefined => {
  const toUtcMidnight = (token?: string | null): number | undefined => {
    const match = token?.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return undefined
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  const fromMs = toUtcMidnight(from)
  const toMs = toUtcMidnight(to)
  if (fromMs === undefined || toMs === undefined) return undefined
  return Math.round((toMs - fromMs) / 86400000)
}

export const calendarDayInZone = (
  timeZone?: string | null,
  now: Date = new Date(),
): string | undefined => {
  if (!timeZone) return undefined

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now)

    const read = (type: string) =>
      parts.find((part) => part.type === type)?.value

    const year = read('year')
    const month = read('month')
    const day = read('day')

    if (!year || !month || !day) return undefined

    return `${year}-${month}-${day}`
  } catch {
    return undefined
  }
}

export default calendarDayInZone
